import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

import {
  MarketingSection,
  SectionKicker,
  SectionTitle,
} from "../MarketingSection";

export type Testimonial = {
  quote: string;
  name: string;
  role: string;
  image: string;
  /** Logos sit in a white tile instead of a round crop. */
  logo?: boolean;
  href?: string;
};

const TINTS = [
  "bg-[linear-gradient(145deg,#fff,#eef0ff)]",
  "bg-[linear-gradient(145deg,#fff,#fff0f7)]",
  "bg-[linear-gradient(145deg,#fff,#f4ffe4)]",
];

export default function Testimonials({
  kicker,
  title,
  items,
  className,
}: {
  kicker: string;
  title: ReactNode;
  items: Testimonial[];
  className?: string;
}) {
  return (
    <MarketingSection className={cn("bg-white", className)}>
      <SectionKicker>{kicker}</SectionKicker>
      <SectionTitle className="[&_span]:text-blue-600">{title}</SectionTitle>
      <div className="mt-[46px] grid grid-cols-3 gap-[22px] max-[680px]:grid-cols-1">
        {items.map((item, i) => {
          const image = (
            <img
              src={item.image}
              alt={item.logo ? `${item.name} logo` : item.name}
              loading="lazy"
              className={cn(
                "size-[54px] shrink-0",
                item.logo
                  ? "rounded-[12px] border border-marketing-line bg-white object-contain p-1"
                  : "rounded-full object-cover",
              )}
            />
          );
          return (
            <article
              key={item.name}
              className={cn(
                "flex min-h-[300px] flex-col rounded-[24px] border border-marketing-line p-[34px] shadow-[0_12px_34px_rgba(15,26,75,0.07)]",
                TINTS[i % 3],
              )}
            >
              <blockquote className="m-0 mb-8 font-figtree text-[16px] font-medium italic leading-[1.58] text-[#596176]">
                “{item.quote}”
              </blockquote>
              <div className="mt-auto flex items-center gap-[14px]">
                {item.href ? (
                  <a href={item.href} className="shrink-0 rounded-full">
                    {image}
                  </a>
                ) : (
                  image
                )}
                <div className="font-open-sans">
                  {item.href ? (
                    <a
                      href={item.href}
                      className="block text-[16px] font-bold text-marketing-navy hover:underline hover:underline-offset-[3px]"
                    >
                      {item.name}
                    </a>
                  ) : (
                    <strong className="block text-[16px] text-marketing-navy">
                      {item.name}
                    </strong>
                  )}
                  <span className="mt-[3px] block text-[14px] text-marketing-muted">
                    {item.role}
                  </span>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </MarketingSection>
  );
}
